import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { trackDependency, trackException, getAppInsights } from './appInsights';

interface RequestMetadata {
  startTime: number;
  requestId: string;
}

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata;
  }
}

const getBaseURL = () => {
  const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (envBaseUrl) {
    return `${envBaseUrl}/api/`;
  }

  const protocol = window.location.protocol;
  const host = window.location.host;
  return `${protocol}//${host}/api/`;
};

const generateUuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const generateRequestId = (): string => {
  return `|${generateUuid()}.`;
};

export const AUTH_EXPIRED_EVENT = 'auth:expired';

const DefaultRetryAfterSeconds = 60;

function retryAfterSecondsOf(response: AxiosResponse | undefined): number {
  const header = String(response?.headers?.['retry-after'] ?? '');
  if (/^\d+$/.test(header)) return Math.max(1, Number(header));
  const body = (response?.data as { retryAfterSeconds?: unknown } | undefined)?.retryAfterSeconds;
  if (typeof body === 'number' && Number.isFinite(body)) return Math.max(1, Math.ceil(body));
  return DefaultRetryAfterSeconds;
}

/**
 * The server answered 429: the user sent too many requests, and the same request is accepted again after
 * `retryAfterSeconds`. Every API call rejects with it instead of the plain `AxiosError` it extends.
 */
export class RateLimitedError extends AxiosError {
  readonly retryAfterSeconds: number;

  constructor(error: AxiosError) {
    super(error.message, error.code, error.config, error.request, error.response);
    this.name = 'RateLimitedError';
    this.retryAfterSeconds = retryAfterSecondsOf(error.response);
  }
}

const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true
});

// Requests that must never trigger a silent refresh retry (they ARE the auth flow).
const isAuthExempt = (url?: string): boolean => {
  if (!url) return false;
  return ['auth/refresh', 'auth/login', 'auth/register', 'auth/google', 'auth/forgot-password', 'auth/reset-password'].some((p) => url.includes(p));
};

let renewal: Promise<void> | null = null;

/**
 * Renews the session cookie with the refresh token. Every caller in this tab that arrives while a renewal is in
 * flight shares it. When the renewal fails the returned promise rejects; only a 401, which means the refresh token
 * is no longer valid, also signs the user out.
 */
export function renewSession(): Promise<void> {
  if (!renewal) {
    renewal = api
      .post('auth/refresh')
      .then(
        () => undefined,
        (error: unknown) => {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
          }
          throw error;
        },
      )
      .finally(() => {
        renewal = null;
      });
  }
  return renewal;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.metadata = {
    startTime: performance.now(),
    requestId: generateRequestId()
  };

  const appInsights = getAppInsights();
  if (appInsights) {
    const traceparent = appInsights.context?.telemetryTrace?.traceID;
    if (traceparent) {
      config.headers = config.headers ?? {};
      config.headers['Request-Id'] = config.metadata.requestId;
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const config = response.config;
    if (config.metadata) {
      const duration = performance.now() - config.metadata.startTime;
      const url = `${config.baseURL || ''}${config.url || ''}`;

      trackDependency(
        config.metadata.requestId,
        config.method?.toUpperCase() || 'GET',
        url,
        duration,
        true,
        response.status,
        {
          responseSize: JSON.stringify(response.data)?.length?.toString() || '0'
        }
      );
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config;
    if (config?.metadata) {
      const duration = performance.now() - config.metadata.startTime;
      const url = `${config.baseURL || ''}${config.url || ''}`;
      const status = error.response?.status || 0;

      trackDependency(
        config.metadata.requestId,
        config.method?.toUpperCase() || 'GET',
        url,
        duration,
        false,
        status,
        {
          errorMessage: error.message
        }
      );

      if (status >= 500 || status === 0) {
        trackException(error, {
          url,
          method: config.method?.toUpperCase() || 'GET',
          status: status.toString()
        });
      }
    }

    if (error.response?.status === 429) {
      return Promise.reject(new RateLimitedError(error));
    }

    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthExempt(originalRequest.url)
    ) {
      originalRequest._retry = true;
      try {
        await renewSession();
      } catch {
        return Promise.reject(error);
      }
      return api(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default api;
